// SUMMARY: Encodes/decodes attachment context inside a chat message. Documents embed their extracted
// text; images embed a data URL (for the viewer + persistence). The frontend parses these back out to
// render chips (and an in-chat viewer), so raw transcription/data never shows in the bubble. The wire
// form sent over the network keeps images OUT (they travel separately) to stay under the size cap.

export interface ParsedDoc {
	name: string
	text: string
}
export interface ParsedImage {
	name: string
	dataUrl: string
}
export interface ParsedMessage {
	docs: ParsedDoc[]
	images: ParsedImage[]
	text: string
}

const DOC_BLOCK = /\[Attachment: ([^\]]+)\]\n([\s\S]*?)\n\[End of attachment\]/g
const IMG_MD = /!\[([^\]]*)\]\((data:image\/[^)]+)\)/g

// ===============================
// SECTION: BUILD
// ===============================

/** Document text blocks + the user's typed text — the small form sent over the wire (no image bytes) */
export function buildWireMessage(options: { docs: ParsedDoc[]; text: string }): string {
	const blocks = options.docs.map((d) => `[Attachment: ${d.name}]\n${d.text}\n[End of attachment]`)
	const prefix = blocks.join('\n\n')
	const text = options.text.trim()
	if (!prefix) return text
	return text ? `${prefix}\n\n${text}` : prefix
}

/** Markdown image embeds for the given images (data URLs) */
export function imageMarkdown(images: ParsedImage[]): string {
	return images.map((i) => `![${i.name}](${i.dataUrl})`).join('\n\n')
}

/** Full form with image data URLs embedded — used for the visible/stored message (viewer + reload) */
export function buildDisplayMessage(options: { docs: ParsedDoc[]; images: ParsedImage[]; text: string }): string {
	return [imageMarkdown(options.images), buildWireMessage(options)].filter(Boolean).join('\n\n')
}

// ===============================
// SECTION: PARSE (frontend display + viewer)
// ===============================

export function parseMessageContent(content: string): ParsedMessage {
	const docs: ParsedDoc[] = []
	for (const m of content.matchAll(DOC_BLOCK)) docs.push({ name: m[1], text: m[2] })

	const images: ParsedImage[] = []
	for (const m of content.matchAll(IMG_MD)) images.push({ name: m[1] || 'image', dataUrl: m[2] })

	const text = content
		.replace(DOC_BLOCK, '')
		.replace(IMG_MD, '')
		.replace(/\n{3,}/g, '\n\n')
		.trim()

	return { docs, images, text }
}
